import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from 'app/variables';

interface ExpandableTextProps extends React.ComponentPropsWithoutRef<'div'> {
  text: string;
  buttonPosition: 'start' | 'center' | 'end';
  maxLines: number;
}

const Container = styled.div`
  position: relative;
`;

const Text = styled.div<{ $expandable: boolean }>`
  ${({ $expandable }) =>
    $expandable &&
    `
    overflow: hidden;
    min-height: 3.75rem;
  `}
  line-height: 1.25;
`;

const ExpandTextButton = styled.div<{
  $position: ExpandableTextProps['buttonPosition'];
  $expanded: boolean;
}>`
  cursor: pointer;
  position: relative;
  display: flex;
  justify-content: ${({ $position }) =>
    $position === 'start'
      ? 'flex-start'
      : $position === 'end'
        ? 'flex-end'
        : 'center'};
  color: ${colors.pacificBlue};
  font-size: 0.8rem;
  text-align: center;
  width: fit-content;
  padding: 0.25rem 0;

  &::after {
    border: 1.5px solid currentColor;
    border-right: 0;
    border-top: 0;
    content: ' ';
    display: block;
    height: 9px;
    margin-left: 7px;
    pointer-events: none;
    transform: rotate(-45deg);
    transform-origin: center;
    width: 9px;
  }

  ${({ $expanded }) =>
    $expanded &&
    `
      &::after {
        transform: rotate(135deg);
        margin-top: 6px;
      }
    `}
`;

const TextContainer = styled.div<{ $expanded: boolean }>`
  display: grid;
  transition: grid-template-rows 0.5s;
  grid-template-rows: 0fr;

  ${({ $expanded }) =>
    $expanded
      ? `
      grid-template-rows: 1fr;
    `
      : `
      transition-delay: 0.3s;
    `}
`;

const ExpandableText = ({
  text,
  buttonPosition = 'center',
  maxLines = 5,
  ...rest
}: ExpandableTextProps) => {
  const [showFullDescription, setShowFullDescription] =
    useState<boolean>(false);
  const [expandable, setExpandable] = useState<boolean>(false);
  const textContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const container = textContainerRef.current;
    if (!container) return;

    const containerHeight = container.scrollHeight;
    const lineHeight = parseFloat(
      window.getComputedStyle(container).lineHeight
    );
    const lines = Math.round(containerHeight / lineHeight);
    setExpandable(lines >= maxLines);
  }, [text, maxLines]);

  const expandTextButtonText = showFullDescription ? 'Show less' : 'Show more';
  return (
    <Container {...rest}>
      <TextContainer $expanded={showFullDescription}>
        <Text $expandable={expandable} ref={textContainerRef}>
          {text}
        </Text>
        {expandable && (
          <ExpandTextButton
            $position={buttonPosition}
            onClick={() => setShowFullDescription(!showFullDescription)}
            $expanded={showFullDescription}
          >
            {expandTextButtonText}
          </ExpandTextButton>
        )}
      </TextContainer>
    </Container>
  );
};

export default ExpandableText;
